import { SEO } from "../components/SEO";

export function Terms() {
  return (
    <>
      <SEO
        title="Terms of Service | StarSinger MCP"
        description="Terms of Service for the StarSinger MCP API. Read about API usage, licensing, rate limits, and prohibited uses."
        pathname="/terms"
      />
      <div className="max-w-3xl mx-auto px-6 py-20">
      <h1 className="text-3xl font-bold text-white mb-2">
        StarSinger MCP API — Terms of Service
      </h1>
      <p className="text-white/40 text-sm mb-10">Last updated: September 27, 2026</p>

      <div className="space-y-8 text-white/70 leading-relaxed text-[15px]">
        <section>
          <h2 className="text-xl font-semibold text-white mb-3">1. Acceptance</h2>
          <p>
            By accessing or using the StarSinger MCP API ("API"), you agree to be
            bound by these Terms of Service ("Terms"). The API is operated by
            Veronata, Inc. ("we", "us", "our"). If you do not agree, do not use
            the API.
          </p>
          <p className="mt-3">
            These Terms govern your use of the StarSinger MCP API specifically.
            They are separate from and supersede the general StarSinger.ai Terms
            of Service with respect to API access.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">2. Description of Service</h2>
          <p>
            The StarSinger MCP API provides programmatic access to a catalog of
            AI-generated music tracks, artist profiles, and related metadata via
            the Model Context Protocol (MCP). The API is designed for integration
            into AI agents, developer tools, and commercial applications.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">3. API Keys and Access</h2>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              You must register and obtain an API key to access the service.
              API keys are personal and may not be shared, sold, or transferred.
            </li>
            <li>
              You are responsible for all activity under your API key. If you
              suspect unauthorized use, revoke the key immediately via your
              dashboard.
            </li>
            <li>
              We reserve the right to disable any API key at any time for
              violation of these Terms, abuse, or any other reason.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">4. Rate Limits</h2>
          <p>API usage is subject to daily request limits based on your plan:</p>
          <ul className="list-disc pl-5 space-y-1 mt-2">
            <li><strong className="text-white">Free:</strong> 100 requests per day</li>
            <li><strong className="text-white">Pro:</strong> 1,000 requests per day</li>
            <li><strong className="text-white">Business:</strong> 10,000 requests per day</li>
            <li><strong className="text-white">Enterprise:</strong> 50,000 requests per day</li>
          </ul>
          <p className="mt-2">
            Exceeding your rate limit will result in HTTP 429 responses until the
            daily window resets at midnight UTC. Attempting to circumvent rate
            limits (e.g., creating multiple accounts) will result in
            immediate termination.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">
            5. Content Ownership and License
          </h2>
          <p>
            All music tracks, audio files, lyrics, album artwork, artist
            profiles, and other content accessible through the API
            ("Content") is owned by Veronata, Inc. and protected by copyright
            and other intellectual property laws.
          </p>

          <h3 className="text-lg font-semibold text-white mt-5 mb-2">
            What You Receive: A Streaming License
          </h3>
          <p>
            API access grants you a <strong className="text-white">limited,
            non-exclusive, revocable, non-transferable license to stream</strong>{" "}
            Content through your application or AI assistant integration. This
            includes giving an end user a playable link to, or an embedded player
            for, an individual track — for example a link returned in an AI
            assistant conversation — so they can listen to it on demand. This is
            fundamentally different from the "Full Commercial Rights" granted to
            StarSinger.ai creators who pay to generate their own music.
          </p>

          <h3 className="text-lg font-semibold text-white mt-5 mb-2">
            License by Tier
          </h3>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              <strong className="text-white">Free tier:</strong> Personal and
              evaluation use only. Attribution to StarSinger required in any
              public-facing usage.
            </li>
            <li>
              <strong className="text-white">Pro tier:</strong> Commercial use
              permitted within your applications. Attribution to StarSinger
              required.
            </li>
            <li>
              <strong className="text-white">Enterprise tier:</strong> Broader
              commercial rights available under separate agreement. Contact
              us for custom licensing.
            </li>
          </ul>

          <h3 className="text-lg font-semibold text-white mt-5 mb-2">
            Important Distinction
          </h3>
          <p>
            StarSinger.ai users who <em>create</em> content using paid credits
            receive Full Commercial Rights to <em>their specific creations</em>.
            MCP API consumers access a shared catalog and receive a{" "}
            <em>streaming license only</em> — not ownership, not commercial
            reproduction rights.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">6. Prohibited Uses</h2>
          <p>You may NOT:</p>
          <ul className="list-disc pl-5 space-y-2 mt-2">
            <li>
              <strong className="text-white">Bulk download or cache</strong>{" "}
              audio files, lyrics, or other Content beyond what is necessary for
              real-time streaming in your application.
            </li>
            <li>
              <strong className="text-white">Offer Content for download or copying</strong>{" "}
              — provide download buttons or offline copies, publish or export lists
              of audio URLs in bulk, or incorporate audio files into other media
              you distribute (videos, podcasts, exported files) without a separate
              agreement. Playable links to individual tracks for on-demand listening
              are permitted (see Section 5).
            </li>
            <li>
              <strong className="text-white">Build a competing service</strong>{" "}
              — use the API to create a music catalog, library, or streaming
              service that competes with StarSinger.
            </li>
            <li>
              <strong className="text-white">Train AI/ML models</strong>{" "}
              on Content accessed through the API, whether your own models or
              third-party models.
            </li>
            <li>
              <strong className="text-white">Create stock content</strong>{" "}
              — redistribute, resell, or make Content available as part of a
              stock music, stock audio, or content licensing service.
            </li>
            <li>
              <strong className="text-white">Circumvent rate limits</strong>{" "}
              or access controls, including creating multiple free accounts.
            </li>
            <li>
              <strong className="text-white">Scrape or harvest</strong>{" "}
              the catalog systematically, including automated enumeration of
              artists, tracks, or search results beyond normal application use.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">7. Attribution</h2>
          <p>
            Free and Pro tier users must include attribution to StarSinger in
            any application or service that surfaces Content to end users.
            Acceptable attribution includes:
          </p>
          <ul className="list-disc pl-5 space-y-1 mt-2">
            <li>"Music by StarSinger" with a link to starsinger.ai</li>
            <li>"Powered by StarSinger MCP" in your application footer or credits</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">8. Termination</h2>
          <p>
            We may suspend or terminate your API access at any time, with or
            without notice, for any reason, including violation of these Terms.
            Upon termination, your license to stream Content is immediately
            revoked and you must delete any cached Content.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">9. Disclaimer of Warranties</h2>
          <p>
            THE API AND CONTENT ARE PROVIDED "AS IS" WITHOUT WARRANTY OF ANY
            KIND. WE DO NOT GUARANTEE UPTIME, AVAILABILITY, OR ACCURACY OF
            CONTENT. AI-GENERATED MUSIC MAY CONTAIN IMPERFECTIONS.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">10. Limitation of Liability</h2>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, VERONATA, INC. SHALL NOT BE
            LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR
            PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE API.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">11. Changes to Terms</h2>
          <p>
            We may update these Terms at any time. Continued use of the API
            after changes constitutes acceptance. Material changes will be
            communicated via email to the address associated with your API key.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">12. Governing Law</h2>
          <p>
            These Terms are governed by the laws of the State of California,
            United States, without regard to conflict of law principles.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-white mb-3">13. Contact</h2>
          <p>
            Questions about these Terms? Contact us at{" "}
            <a href="mailto:legal@starsinger.ai" className="text-amber-400 hover:text-amber-300">
              legal@starsinger.ai
            </a>
          </p>
        </section>
      </div>
      </div>
    </>
  );
}
